import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Star, Users, GitFork, Clock, Gift, Award, ChevronRight, Check, TrendingUp, Shield, Bell, Home, Heart } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const testimonials = [
  {
    name: "Sarah Mitchell",
    role: "Senior Loan Officer",
    company: "Pacific Lending Group",
    text: "Better Closings has transformed how we handle post-close relationships. Our review count has increased dramatically, and the referrals keep coming in.",
    rating: 5
  },
  {
    name: "Michael Chen",
    role: "Branch Manager",
    company: "Summit Mortgage Partners",
    text: "The white-glove service is exactly what our clients expect from us. It's like having an extension of our team without the overhead.",
    rating: 5
  },
  {
    name: "Jennifer Rodriguez",
    role: "Mortgage Broker",
    company: "Elite Home Loans",
    text: "I was skeptical at first, but the results speak for themselves. My clients love the personal touch, and I love the referrals.",
    rating: 5
  }
];

const benefitCards = [
  {
    icon: Users,
    title: "Client Retention",
    description: "Stay top-of-mind without lifting a finger. We ensure your clients remember your name when they're ready to buy or refer.",
    span: "md:col-span-2"
  },
  {
    icon: Star,
    title: "Review Generation",
    description: "Systematic 5-star review collection designed to boost your online reputation.",
    span: "md:col-span-1"
  },
  {
    icon: GitFork,
    title: "Referral Engine",
    description: "Turn one closing into many with our proven referral request system.",
    span: "md:col-span-1"
  },
  {
    icon: Clock,
    title: "Time Saved",
    description: "Focus on what you do best—closing loans. We handle the follow-up.",
    span: "md:col-span-1"
  },
  {
    icon: Gift,
    title: "Gift Coordination",
    description: "Delight your clients with thoughtful gifts they actually want to receive.",
    span: "md:col-span-1"
  }
];

const howItWorksSteps = [
  { number: "01", title: "Submit", description: "Send us closed loan details" },
  { number: "02", title: "Connect", description: "We reach out to your client" },
  { number: "03", title: "Delight", description: "Gift coordination & follow-up" },
  { number: "04", title: "Harvest", description: "Reviews & referrals captured" },
  { number: "05", title: "Deliver", description: "Referrals routed back to you" }
];

export const HomePage = () => {
  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1707047754373-ba65fd3f249f?crop=entropy&cs=srgb&fm=jpg&q=85')"
          }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            {/* Gold accent line */}
            <motion.div variants={fadeInUp} className="h-[2px] w-16 bg-[#C9A227] mb-8" />
            
            <motion.p variants={fadeInUp} className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4 font-medium">
              Post-Closing Concierge Service
            </motion.p>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.1] mb-6"
            >
              Close Loans.<br />
              <span className="text-[#E5D5A5]">Build Legacy.</span><br />
              We Handle the Rest.
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-neutral-300 leading-relaxed mb-10 max-w-2xl"
            >
              The premier post-closing concierge for elite mortgage professionals. 
              Elevate your client experience with white-glove follow-up that drives 
              reviews, retention, and referrals.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="hero-cta-primary">
                <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-8 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  Book a Demo
                </Button>
              </Link>
              <Link to="/how-it-works" data-testid="hero-cta-secondary">
                <Button variant="outline" className="bg-transparent border-2 border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 rounded-none px-8 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  How It Works
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Who We Help Section */}
      <section data-testid="who-we-help-section" className="bg-[#0F0F0F] py-16 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-neutral-500 text-xs tracking-[0.2em] uppercase mb-8">
            Trusted by Top Producers
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-neutral-400">
            <span className="text-sm">Mortgage Brokers</span>
            <span className="text-[#C9A227]">•</span>
            <span className="text-sm">Loan Officers</span>
            <span className="text-[#C9A227]">•</span>
            <span className="text-sm">Lender Partners</span>
            <span className="text-[#C9A227]">•</span>
            <span className="text-sm">Branch Managers</span>
          </div>
        </div>
      </section>

      {/* Client Benefits Section */}
      <section data-testid="client-benefits-section" className="bg-[#141414] py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">For Your Clients</p>
            <h2 className="font-serif text-3xl md:text-5xl font-semibold text-white mb-6">
              Benefits That <span className="text-[#E5D5A5]">Clients Love</span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto">
              Your clients don't just get a transaction—they get a lasting relationship and 
              exclusive benefits that add real value to their homeownership journey.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Gift,
                title: "Personalized Closing Gift",
                description: "Every client receives a thoughtful closing gift of their choice to celebrate their new home—a memorable touch that shows you care."
              },
              {
                icon: TrendingUp,
                title: "Lifetime Loan Monitoring",
                description: "Clients are enrolled in our lifetime loan tracking system. When rates drop or equity opportunities arise, we proactively reach out to help them save money."
              },
              {
                icon: Shield,
                title: "Dedicated Support",
                description: "A personal concierge handles any post-closing questions, ensuring clients feel supported long after the keys are handed over."
              },
              {
                icon: Bell,
                title: "Rate Watch Alerts",
                description: "Automatic notifications when refinancing could benefit them, so they never miss an opportunity to lower their monthly payment."
              },
              {
                icon: Home,
                title: "Home Value Updates",
                description: "Periodic updates on their home's estimated value, helping clients stay informed about their biggest investment."
              },
              {
                icon: Heart,
                title: "VIP Referral Rewards",
                description: "When clients refer friends and family, they become part of our VIP network with exclusive perks and priority service."
              }
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`client-benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-[#1A1A1A] border border-white/5 p-6 group hover:border-[#C9A227]/30 transition-all duration-500"
              >
                <benefit.icon className="w-10 h-10 text-[#C9A227] mb-4" strokeWidth={1.5} />
                <h3 className="font-serif text-lg text-white mb-2">{benefit.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section - Bento Grid */}
      <section data-testid="benefits-section" className="bg-[#0F0F0F] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Why Better Closings</p>
            <h2 className="font-serif text-3xl md:text-5xl font-semibold text-white mb-6">
              Elevate Every <span className="text-[#E5D5A5]">Client Experience</span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto">
              We handle the post-close experience so you get more reviews, stronger retention, 
              and more referrals—without adding work to your team.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {benefitCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`benefit-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
                className={`bg-[#1A1A1A] border border-white/5 p-8 relative overflow-hidden group hover:border-[#C9A227]/30 transition-all duration-500 ${card.span}`}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A227]/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                <card.icon className="w-10 h-10 text-[#C9A227] mb-6" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-white mb-3">{card.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{card.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Feature Section */}
      <section data-testid="visual-feature-section" className="bg-[#141414] py-24 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">The White Glove Standard</p>
              <h2 className="font-serif text-3xl md:text-5xl font-semibold text-white mb-6">
                Your Clients Deserve <span className="text-[#E5D5A5]">More</span>
              </h2>
              <p className="text-neutral-300 text-lg leading-relaxed mb-8">
                Your clients deserve more than a generic email. They deserve a personal concierge. 
                Our team bridges the gap between closing and your next transaction, ensuring every 
                client feels valued and remembered.
              </p>
              <ul className="space-y-4 mb-8">
                {['Personalized follow-up calls', 'Thoughtful gift coordination', 'Professional review requests', 'Referral capture & routing'].map((item) => (
                  <li key={item} className="flex items-center text-neutral-300">
                    <Check className="w-5 h-5 text-[#C9A227] mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/services" data-testid="visual-feature-cta">
                <Button variant="ghost" className="text-[#C9A227] hover:text-[#E5D5A5] p-0 font-semibold text-sm tracking-wider uppercase group">
                  Explore Our Services
                  <ChevronRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-[#C9A227]/10 blur-3xl" />
              <img
                src="https://images.pexels.com/photos/8276364/pexels-photo-8276364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="White glove service"
                className="relative w-full h-[400px] object-cover border border-white/10"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Preview */}
      <section data-testid="how-it-works-preview-section" className="bg-[#0F0F0F] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Simple Process</p>
            <h2 className="font-serif text-3xl md:text-5xl font-semibold text-white mb-6">
              How It <span className="text-[#E5D5A5]">Works</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {howItWorksSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`how-it-works-step-${step.number}`}
                className="text-center group"
              >
                <div className="text-[#C9A227] font-serif text-3xl md:text-4xl font-bold mb-3 group-hover:text-[#E5D5A5] transition-colors">
                  {step.number}
                </div>
                <h4 className="font-serif text-lg text-white mb-2">{step.title}</h4>
                <p className="text-neutral-500 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-12"
          >
            <Link to="/how-it-works" data-testid="how-it-works-cta">
              <Button variant="outline" className="bg-transparent border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 rounded-none px-8 py-4 font-semibold text-sm tracking-wider uppercase transition-all duration-300">
                Learn More
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="bg-[#141414] py-24 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Testimonials</p>
            <h2 className="font-serif text-3xl md:text-5xl font-semibold text-white">
              What Our <span className="text-[#E5D5A5]">Partners Say</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`testimonial-${index}`}
                className="bg-[#1A1A1A] border border-white/5 p-8 hover:border-[#C9A227]/30 transition-all duration-500"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-[#C9A227] fill-[#C9A227]" />
                  ))}
                </div>
                <p className="text-neutral-300 text-sm leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="text-white font-medium">{testimonial.name}</p>
                  <p className="text-neutral-500 text-sm">{testimonial.role}</p>
                  <p className="text-[#C9A227] text-xs">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section data-testid="pricing-teaser-section" className="bg-[#0F0F0F] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="max-w-2xl mx-auto bg-gradient-to-b from-[#1A1A1A] to-[#0F0F0F] border border-[#C9A227]/50 p-12 shadow-[0_0_30px_rgba(201,162,39,0.1)]">
              <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-6">Simple Pricing</p>
              <div className="flex items-baseline justify-center mb-4">
                <span className="font-serif text-6xl md:text-7xl font-bold text-white">$249</span>
                <span className="text-neutral-400 ml-3">/ closed loan</span>
              </div>
              <p className="text-neutral-400 mb-8">Flat fee. No subscriptions. No hidden costs.</p>
              <div className="grid grid-cols-2 gap-4 mb-8 text-left">
                {['Dedicated Concierge', '30-Day Follow-up', 'Gift Coordination', 'Review Collection'].map((feature) => (
                  <div key={feature} className="flex items-center text-neutral-300 text-sm">
                    <Check className="w-4 h-4 text-[#C9A227] mr-2 flex-shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>
              <Link to="/pricing" data-testid="pricing-teaser-cta">
                <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-8 py-4 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  View Full Pricing
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section data-testid="final-cta-section" className="bg-[#1A1A1A] py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Award className="w-16 h-16 text-[#C9A227] mx-auto mb-8" strokeWidth={1} />
            <h2 className="font-serif text-3xl md:text-5xl font-semibold text-white mb-6">
              Ready to <span className="text-[#E5D5A5]">Elevate</span> Your Business?
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-10">
              Join the elite mortgage professionals who trust Better Closings to deliver 
              an exceptional post-close experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="final-cta-primary">
                <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-10 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  Book Your Demo Today
                </Button>
              </Link>
              <Link to="/partners" data-testid="final-cta-secondary">
                <Button variant="outline" className="bg-transparent border border-white/20 text-white hover:border-[#C9A227] hover:text-[#C9A227] rounded-none px-10 py-6 font-semibold text-sm tracking-wider uppercase transition-all duration-300">
                  Learn About Partnership
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
